import { Client } from "@stomp/stompjs";
import { useEffect, useRef, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";
import { BiSolidPhoneCall } from "react-icons/bi";
import { useNavigate } from "react-router-dom";
import { UseConsultant } from "../context/ConsultantContext";
import { audioServerUrl, vasthuUrl } from "./Api";
import Toast from "./Toast";

const DataCall = () => {
  const { handleWalletCall, handleFreeCall } = UseConsultant();
  const role = window.sessionStorage.getItem("role") || "ROLE_USER";
  const userId = window.sessionStorage.getItem("userId");
  const token = window.sessionStorage.getItem("token");

  const navigate = useNavigate();

  const localAudioRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const stompRef = useRef(null);
  const pcRef = useRef(null);
  const localStreamRef = useRef(null);
  const iceQueueRef = useRef([]);
  const dataRef = useRef(null);
  const userRef = useRef(null);
  const consultantRef = useRef(null);

  const [myId, setMyId] = useState("");
  const [otherId, setOtherId] = useState("");
  const [callData, setCallData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [connected, setConnected] = useState(false);
  const [callActive, setCallActive] = useState(false);
  const [countDown, setCountDown] = useState(0);

  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState("");
  const [toastTrigger, setToastTrigger] = useState(0);

  const alert = (msg) => {
    setMessage(msg);
    setToastTrigger((prev) => prev + 1);
  };

  const [openModal, setOpenModal] = useState(false);
  const showModal = () => setOpenModal(true);
  const closeModal = () => {
    setOpenModal(false);
    window.history.pushState(null, "", window.location.href);
  };
  const handleBackPage = () => hangUp();

  // --------- Back button / unload
  useEffect(() => {
    const handleBackButton = (e) => {
      e.preventDefault();
      showModal();
      window.history.pushState(null, "", window.location.href);
    };
    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", handleBackButton);
    return () => window.removeEventListener("popstate", handleBackButton);
  }, []);

  useEffect(() => {
    const handleBeforeUnload = (e) => {
      e.preventDefault();
      hangUp();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, []);

  // --------- Fetch call details
  useEffect(() => {
    const fetchCallDetails = async () => {
      try {
        setLoading(true);
        const endpoint =
          role === "ROLE_USER"
            ? `/datacall/getCalldetails/${userId}`
            : `/datacall/getCallDetailsForConsultant/${userId}`;
        const res = await vasthuUrl.get(endpoint, {
          headers: { Authorization: `Bearer ${token}` },
        });

        setCallData(res.data);
        dataRef.current = res.data;
        userRef.current = res.data.callFromId;
        consultantRef.current = res.data.callToId;
        setMyId(role === "ROLE_USER" ? res.data.callFromId : res.data.callToId);
        setOtherId(
          role === "ROLE_USER" ? res.data.callToId : res.data.callFromId
        );

        if (res.data?.proposedChatDuration) {
          setCountDown(Number(res.data.proposedChatDuration));
        }
      } catch (err) {
        alert("Failed to fetch call details", "error");
        setMessageType("error");
      } finally {
        setLoading(false);
      }
    };
    fetchCallDetails();
  }, [userId]);

  // --------- Auto hangup after 2 minutes if not connected
  useEffect(() => {
    if (callActive) return;
    const timer = setTimeout(() => {
      console.log("Call not connected within 2 minutes, terminating...");
      alert("Consultant is busy now. Please try to few minutes later", "error");
      setMessageType("error");
      hangUp();
      // if (dataRef.current.type === "WALLET") handleWalletCall();
      // else handleFreeCall();
    }, 120000);
    return () => clearTimeout(timer);
  }, [callActive]);

  // --------- STOMP & WebSocket
  useEffect(() => {
    if (!myId) return;

    const client = new Client({
      brokerURL: audioServerUrl,
      reconnectDelay: 5000,
      debug: (str) => console.log("STOMP:", str),
      onConnect: () => {
        console.log("STOMP connected");
        setConnected(true);

        // ---- FIX: Fully reset remote audio stream ----
if (remoteAudioRef.current) {
  const emptyStream = new MediaStream();
  remoteAudioRef.current.srcObject = emptyStream;

  remoteAudioRef.current.pause();
  remoteAudioRef.current.load();

  remoteAudioRef.current
    .play()
    .catch((e) => console.warn("Remote audio play error:", e));
}

        // subscribe to user queue
        client.subscribe(`/user/${myId}/queue/call`, async (msg) => {
          try {
            const data = JSON.parse(msg.body);
            console.log("SIGNAL IN:", data);

            if (data.type === "offer" && role === "ROLE_USER") {
              await onReceiveOffer(data);
            } else if (data.type === "answer" && role === "ROLE_PROFESSIONAL") {
              if (pcRef.current) {
                await pcRef.current.setRemoteDescription(
                  new RTCSessionDescription(data.sdp)
                );
                setCallActive(true);
                // flush queued ICE
                for (const c of iceQueueRef.current) {
                  await pcRef.current.addIceCandidate(new RTCIceCandidate(c));
                }
                iceQueueRef.current = [];
              }
            } else if (data.type === "ice") {
              if (!pcRef.current || !pcRef.current.remoteDescription) {
                iceQueueRef.current.push(data.candidate);
              } else {
                try {
                  await pcRef.current.addIceCandidate(
                    new RTCIceCandidate(data.candidate)
                  );
                } catch (e) {
                  console.warn("ICE add error:", e);
                }
              }
            } else if (data.type === "hangup") hangUp();
          } catch (e) {
            console.error("Signal error:", e);
          }
        });

        if (role === "ROLE_PROFESSIONAL")
          setTimeout(() => startAsConsultant(), 500);
      },
      onStompError: (frame) =>
        console.error("STOMP error:", frame.headers["message"]),
      onWebSocketClose: () => setConnected(false),
    });

    stompRef.current = client;
    client.activate();

    return () => {
      try {
        client.deactivate();
      } catch (e) {}
      if (pcRef.current) pcRef.current.close();
      if (localStreamRef.current)
        localStreamRef.current.getTracks().forEach((t) => t.stop());
    };
  }, [myId, role]);

  // --------- Local stream
  const getLocalStream = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      localStreamRef.current = stream;

      if (localAudioRef.current) {
        localAudioRef.current.muted = true;
        localAudioRef.current.srcObject = stream;
        await localAudioRef.current
          .play()
          .catch((e) => console.warn("Local audio play failed", e));
      }

      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = new MediaStream(); // placeholder
        remoteAudioRef.current
          .play()
          .catch((e) => console.warn("Remote audio play failed", e));
      }

      return stream;
    } catch (err) {
      console.warn("Mic permission denied:", err);
      const dummy = new MediaStream();
      localStreamRef.current = dummy;
      return dummy;
    }
  };

  // --------- Peer Connection
  const createPeer = (remoteUserId) => {
    if (!pcRef.current) {
      pcRef.current = new RTCPeerConnection({
        iceServers: [{ urls: "stun:stun.l.google.com:19302" }],
      });

      pcRef.current.ontrack = (ev) => {
        console.log("ontrack", ev);
        if (remoteAudioRef.current) {
          remoteAudioRef.current.srcObject = ev.streams[0];
          remoteAudioRef.current
            .play()
            .catch((e) => console.warn("Remote play error", e));
        }
      };

      pcRef.current.onicecandidate = (ev) => {
        if (ev.candidate && stompRef.current?.connected) {
          stompRef.current.publish({
            destination: "/app/call-signal",
            body: JSON.stringify({
              type: "ice",
              from: myId,
              to: remoteUserId,
              candidate: ev.candidate,
            }),
          });
        }
      };
    }
    return pcRef.current;
  };

  // --------- Start call (consultant)
  const startAsConsultant = async () => {
    const local = await getLocalStream();
    const pc = createPeer(userRef.current);
    local.getTracks().forEach((track) => pc.addTrack(track, local));

    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);

    if (stompRef.current?.connected) {
      stompRef.current.publish({
        destination: "/app/call-signal",
        body: JSON.stringify({
          type: "offer",
          from: consultantRef.current,
          to: userRef.current,
          sdp: offer,
        }),
      });
      console.log("Offer sent");
    }

    try {
      await vasthuUrl.put(
        `/datacall/updateCallStatus/${dataRef.current.id}`,
        null,
        { params: { isConnected: true } }
      );
    } catch (err) {
      console.error(err);
    }
  };

  // --------- Receive offer (user)
  const onReceiveOffer = async (data) => {
    const local = await getLocalStream();
    const pc = createPeer(data.from);
    local.getTracks().forEach((track) => pc.addTrack(track, local));

    await pc.setRemoteDescription(new RTCSessionDescription(data.sdp));

    for (const c of iceQueueRef.current) {
      try {
        await pc.addIceCandidate(new RTCIceCandidate(c));
      } catch (e) {
        console.warn(e);
      }
    }
    iceQueueRef.current = [];

    const answer = await pc.createAnswer();
    await pc.setLocalDescription(answer);

    if (stompRef.current?.connected) {
      stompRef.current.publish({
        destination: "/app/call-signal",
        body: JSON.stringify({
          type: "answer",
          from: userRef.current,
          to: consultantRef.current,
          sdp: answer,
        }),
      });
      console.log("Answer sent");
    }

    setCallActive(true);
  };

  // --------- Hangup
  const hangUp = async () => {
    try {
      await vasthuUrl.put(`/datacall/disconnect`, {
        id: dataRef.current.id,
        interactionType: dataRef.current.type,
      });
    } catch (err) {
      console.error(err);
    }

    if (stompRef.current?.connected) {
      stompRef.current.publish({
        destination: "/app/call-signal",
        body: JSON.stringify({ type: "hangup", from: myId, to: otherId }),
      });
    }

    if (localStreamRef.current)
      localStreamRef.current.getTracks().forEach((t) => t.stop());
    localStreamRef.current = null;

    if (pcRef.current) pcRef.current.close();
    pcRef.current = null;

    if (stompRef.current) {
      stompRef.current.deactivate();
      stompRef.current = null;
    }

    setCallActive(false);
    setTimeout(() => navigate(role === "ROLE_USER" ? "/audiocall" : "/"), 2000);
  };

  const formatTime = (seconds) => {
    const hrs = String(Math.floor(seconds / 3600)).padStart(2, "0");
    const mins = String(Math.floor((seconds % 3600) / 60)).padStart(2, "0");
    const secs = String(seconds % 60).padStart(2, "0");
    return `${hrs}:${mins}:${secs}`;
  };

  useEffect(() => {
    if (!callActive) return;
    if (role === "ROLE_PROFESSIONAL" && countDown <= 0) hangUp();
    const timer = setInterval(
      () => setCountDown((prev) => Math.max(prev - 1, 0)),
      1000
    );
    return () => clearInterval(timer);
  }, [callActive, countDown]);

  return (
    <>
      <Toast msg={message} type={messageType} trigger={toastTrigger} />

      {loading ? (
        <div className="text-center">
          <Spinner animation="border" variant="warning" />
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            padding: 20,
            backgroundColor: "#536b81",
            height: "100vh",
          }}
        >
          {/* <h3>Role: {role}</h3>
          <p>My ID: {myId}</p>
          <p>Other ID: {otherId}</p> */}

          <div className="text-center">
            <h2 style={{ color: "red", height: 24 }}>
              {formatTime(countDown)}
            </h2>
            <h6 style={{ color: "#fff" }}>Remaining Time</h6>
          </div>

          <div className="my-5">
            <div className="call_proceed">
              <BiSolidPhoneCall size={50} />
            </div>
          </div>

          <div>
            <audio ref={localAudioRef} autoPlay muted playsInline />
            <audio ref={remoteAudioRef} autoPlay playsInline />
          </div>

          <div style={{ marginTop: 12 }}>
            <div>Call connected {connected ? "" : " connecting..."}</div>
            {role === "ROLE_USER" && (
              <div>{callActive ? "" : "Waiting For Consultant"}</div>
            )}
          </div>

          <div style={{ marginTop: 12 }}>
            {callActive && (
              <Button variant="danger" onClick={showModal}>
                END
              </Button>
            )}
          </div>
        </div>
      )}

      <Modal show={openModal} onHide={closeModal} className="all_border">
        <Modal.Header closeButton className="all_background">
          <h3>Confirm Alert</h3>
        </Modal.Header>
        <Modal.Body>
          <h6>Do You want to disconnect the call ?</h6>
        </Modal.Body>
        <Modal.Footer className="d-flex justify-content-around">
          <Button className="all_btn" onClick={closeModal}>
            Cancel
          </Button>
          <Button className="all_btn" onClick={handleBackPage}>
            Yes
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default DataCall;
